import {
  Button,
  CompositionButton,
  DepositToWalletButton,
  DropDownRef,
  InfoAlert,
  ProjectionChartSection,
  Section,
  YieldBearingAssetCompositionSection,
  YieldBearingAssetTvlAndReturnCarouselSection,
} from '@/components';
import { useNavigation, useWalletsContext } from '@/components/providers';
import APP_CONFIG from '@/config/app';
import {
  TokenInterface,
  WalletTokenInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import useSmoothScroll from '@/lib/hooks/useSmoothScroll';
import { formatNumberToAmountInTokenCurrencyString } from '@/utils/formatUtils';
import {
  areAllocationsDifferent,
  calculateProjectionData,
  calculateProjectionSummary,
  convertTokenAmountToUsd,
  generateEthereumNetworkDescription,
  generateWhatThisMeansMessage as generateWhatProjectionMeansMessage,
  replaceTokensInString,
} from '@/utils/functionalUtils';
import { YIELD_BEARING_ASSET_ACTIONS_CARD } from '@/utils/strings';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormProvider as ReactHookFormProvider,
  useForm,
} from 'react-hook-form';
import { YieldBearingAssetDepositFormDataInterface } from './interfaces/YieldBearingAssetActionsInterfaces';
import YieldBearingAssetInvestmentAmountInTokenCurrencyAndUsdInputs from './YieldBearingAssetInvestmentAmountInTokenCurrencyAndUsdInputs';
import YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox from './YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  rootGap: 'gap-sm',
  gapBetweenButtonsInFooter: 'gap-sm',
  gapBetweenListOfTokensAndDepositToWalletButton: 'gap-sm',
};
const sizingConfig = {
  buttonHeight: 'h-xl',
};
interface Props {
  yieldBearingAsset: YieldBearingAssetInterface;
  setProjectionData: (
    data: { value: number; time: string; label: string }[],
  ) => void;
  projectionData: { value: number; time: string; label: string }[];
  depositData: YieldBearingAssetDepositFormDataInterface;
  setDepositData: React.Dispatch<
    React.SetStateAction<YieldBearingAssetDepositFormDataInterface>
  >;
  showProjection?: boolean;
  showHistoricalPerformance?: boolean;
}
const areCompositionsEqual = (prev: any[], next: any[]) => {
  if (!prev || !next) return prev === next;
  if (prev.length !== next.length) return false;
  return prev.every(
    (item, index) =>
      Math.abs(item.composition - next[index].composition) < 0.000001,
  );
};
const YieldBearingAssetInvestmentDepositForm: FC<Props> = ({
  yieldBearingAsset,
  setProjectionData,
  projectionData,
  depositData,
  setDepositData,
  showProjection = false,
  showHistoricalPerformance = true,
}) => {
  const { navigateForward } = useNavigation();
  const [effectiveApy, setEffectiveApy] = useState<number>(0);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<DropDownRef>(null);
  const methods = useForm({
    defaultValues: {
      amount: depositData.amount !== 0 ? depositData.amount.toString() : '',
      tokenId: depositData.token?.id || '',
      quantity: '',
    },
  });
  const {
    register,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
    watch,
    trigger,
    handleSubmit,
    reset,
  } = methods;
  const amount = watch('amount') || '';
  const { getEligibleTokensForDeposit } = useWalletsContext();
  const availableTokens = getEligibleTokensForDeposit();
  const hasTokensAvailableForDeposit = availableTokens.length > 0;
  const getMaxAmountForSelectedToken = useCallback((): number => {
    const selectedToken = depositData.token;
    return selectedToken ? selectedToken.balance : 0;
  }, [depositData.token]);
  const replacements = useMemo(
    () => ({
      yield_bearing_asset_ticker: yieldBearingAsset.ticker,
      yield_bearing_asset_name: yieldBearingAsset.name,
      selected_asset_name: depositData.token?.name || '',
      selected_asset_ticker: depositData.token?.symbol || '',
      available_funds_in_token_currency:
        formatNumberToAmountInTokenCurrencyString(
          getMaxAmountForSelectedToken(),
          depositData.token?.symbol || '',
        ).toString(),
    }),
    [yieldBearingAsset, depositData.token, getMaxAmountForSelectedToken],
  );
  const calculateAndSetProjectionData = useCallback(
    (currentDepositData: YieldBearingAssetDepositFormDataInterface) => {
      const selectedAsset = currentDepositData.token;
      const amount = currentDepositData.amount || 0;
      if (!selectedAsset || amount <= 0) {
        setProjectionData([]);
        setEffectiveApy(0);
        return;
      }
      const usdPrice = selectedAsset.priceInUsd || 1;
      const totalAmountInUsd = amount * usdPrice;
      const {
        projectionData: newProjectionData,
        effectiveApy: newEffectiveApy,
      } = calculateProjectionData(
        currentDepositData.underlyingYieldBearingAssetsAllocation?.length
          ? currentDepositData.underlyingYieldBearingAssetsAllocation
          : yieldBearingAsset.underlyingYieldBearingAssets,
        totalAmountInUsd,
      );
      setProjectionData(newProjectionData);
      setEffectiveApy(newEffectiveApy);
    },
    [yieldBearingAsset],
  );
  const handleNavigatingToAllocationConfiguration = useCallback(() => {
    const amountValue = parseFloat(amount);
    if (!isNaN(amountValue)) {
      setDepositData((prevData) => ({
        ...prevData,
        amount: amountValue,
      }));
    }
    requestAnimationFrame(() => {
      navigateForward('allocation-configuration');
    });
  }, [navigateForward, amount, setDepositData]);
  useEffect(() => {
    reset({
      amount: depositData.amount !== 0 ? depositData.amount.toString() : '',
      tokenId: depositData.token?.id || '',
      quantity: '',
    });
    dropdownRef.current?.setSelectedOption(depositData.token?.id || '');
  }, [depositData.amount, depositData.token, reset]);
  useEffect(() => {
    if (depositData.token) {
      const amount = parseFloat(watch('amount') || '0');
      const amountInUsd = convertTokenAmountToUsd(amount, depositData.token);
      setValue('quantity', amountInUsd.toFixed(2));
    }
  }, [watch('amount'), depositData.token, setValue]);
  useEffect(() => {
    calculateAndSetProjectionData(depositData);
  }, [depositData, calculateAndSetProjectionData]);
  const handleMaxClick = useCallback(() => {
    const selectedAsset = depositData.token;
    if (selectedAsset) {
      const maxAmount = selectedAsset.balance;
      setValue('amount', maxAmount.toString());
      setDepositData((prevData) => ({
        ...prevData,
        amount: maxAmount,
      }));
    }
  }, [depositData.token, setValue, setDepositData]);
  const handleSelectChange = useCallback(
    (selectedToken: WalletTokenInterface | TokenInterface) => {
      const isInWallet = 'balance' in selectedToken;
      const walletToken = isInWallet
        ? (selectedToken as WalletTokenInterface)
        : null;
      const maxAmount = walletToken ? walletToken.balance || 0 : 0;
      const defaultAmount =
        maxAmount * APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE;
      setValue('tokenId', selectedToken.id || '');
      setValue('amount', defaultAmount.toString());
      setDepositData((prevData) => ({
        ...prevData,
        amount: defaultAmount,
        token: isInWallet
          ? (selectedToken as WalletTokenInterface)
          : ({} as WalletTokenInterface),
        underlyingYieldBearingAssetsAllocation:
          prevData.underlyingYieldBearingAssetsAllocation,
      }));
      trigger('amount');
    },
    [setValue, setDepositData, trigger],
  );
  const errorMessages =
    YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM.ERROR_MESSAGES;
  const { smoothScrollTo } = useSmoothScroll();
  const formContainerElement = formContainerRef.current;
  const showErrorAfterScroll = (
    field: 'tokenId' | 'amount',
    message: string,
  ) => {
    if (formContainerElement) {
      smoothScrollTo(formContainerElement, 300, () => {
        setTimeout(() => {
          setError(field, {
            type: 'manual',
            message,
          });
        }, 50);
      });
    }
  };
  const onSubmit = useCallback(
    async (data: any) => {
      if (!hasTokensAvailableForDeposit) return;
      const maxAmount = getMaxAmountForSelectedToken();
      const amountValue = parseFloat(data.amount);
      if (!depositData.token?.id) {
        showErrorAfterScroll(
          'tokenId',
          replaceTokensInString(
            errorMessages.ASSET_SELECTION_FIELD_EMPTY_ERROR_MESSAGE,
            replacements,
          ),
        );
        return;
      }
      if (isNaN(amountValue) || amountValue <= 0 || amountValue > maxAmount) {
        let errorMessage;
        if (amountValue <= 0) {
          errorMessage = replaceTokensInString(
            errorMessages.AMOUNT_FIELD_ZERO_ERROR_MESSAGE,
            replacements,
          );
        } else if (amountValue > maxAmount) {
          errorMessage = replaceTokensInString(
            errorMessages.AMOUNT_FIELD_LESS_THAN_AVAILABLE_ERROR_MESSAGE,
            {
              ...replacements,
              available_funds_in_token_currency:
                formatNumberToAmountInTokenCurrencyString(
                  maxAmount,
                  depositData.token?.symbol || '',
                ),
            },
          );
        } else {
          errorMessage = replaceTokensInString(
            errorMessages.AMOUNT_FIELD_INVALID_ERROR_MESSAGE,
            replacements,
          );
        }
        showErrorAfterScroll('amount', errorMessage);
        return;
      }
      setDepositData({
        ...depositData,
        amount: amountValue,
      });
      navigateForward('deposit-confirmation');
    },
    [
      getMaxAmountForSelectedToken,
      setError,
      setDepositData,
      navigateForward,
      depositData,
      errorMessages,
      replacements,
      hasTokensAvailableForDeposit,
      showErrorAfterScroll,
    ],
  );
  const getProjectedEndAmount = useCallback(() => {
    if (projectionData.length > 0) {
      const endAmount = projectionData[projectionData.length - 1].value;
      return endAmount.toFixed(2);
    }
    return '0.00';
  }, [projectionData]);
  const { startingAmountUSD, endingAmountUSD, totalReturnPercentage } =
    useMemo(() => {
      return calculateProjectionSummary(
        depositData.amount || 0,
        depositData.token?.priceInUsd || 1,
        projectionData,
      );
    }, [depositData, projectionData]);
  const warningTitle = replaceTokensInString(
    YIELD_BEARING_ASSET_ACTIONS_CARD.SUPPORTED_TOKENS_NOT_IN_WALLET_WARNING_TITLE,
    replacements,
  );
  const warningDescription = replaceTokensInString(
    YIELD_BEARING_ASSET_ACTIONS_CARD.SUPPORTED_TOKENS_NOT_IN_WALLET_WARNING_DESCRIPTION,
    replacements,
  );
  const memoizedCompositions = useMemo(() => {
    if (!depositData.underlyingYieldBearingAssetsAllocation?.length) {
      return yieldBearingAsset.underlyingYieldBearingAssets?.map((asset) => ({
        composition: asset.composition,
      }));
    }
    return depositData.underlyingYieldBearingAssetsAllocation?.map((asset) => ({
      composition: asset.composition,
    }));
  }, [
    yieldBearingAsset.underlyingYieldBearingAssets,
    JSON.stringify(
      depositData.underlyingYieldBearingAssetsAllocation?.map(
        (a) => a.composition,
      ),
    ),
  ]);
  const historicalPerformanceCarousel = useMemo(
    () =>
      showHistoricalPerformance && (
        <YieldBearingAssetTvlAndReturnCarouselSection
          asset={yieldBearingAsset}
          customCompositions={memoizedCompositions}
          key={yieldBearingAsset.ticker}
        />
      ),
    [yieldBearingAsset, memoizedCompositions, showHistoricalPerformance],
  );
  return (
    <ReactHookFormProvider {...methods}>
      <div
        ref={formContainerRef}
        className={`flex flex-col flex-grow w-full h-auto 
          ${spacingConfig.rootGap}
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {hasTokensAvailableForDeposit ? (
          <Section
            headerTitle={
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
                .ASSET_SECTION_LABEL
            }
            body={
              <div className={`flex flex-col ${spacingConfig.rootGap}`}>
                <YieldBearingAssetInvestmentTokenSelectionDropDownSelectBox
                  ref={dropdownRef}
                  yieldBearingAsset={yieldBearingAsset}
                  tokenToInvest={depositData.token}
                  handleSelectChange={handleSelectChange}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                />
                <YieldBearingAssetInvestmentAmountInTokenCurrencyAndUsdInputs
                  yieldBearingAsset={yieldBearingAsset}
                  token={depositData.token}
                  amountInTokenCurrency={amount}
                  handleMaxClick={handleMaxClick}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                />
              </div>
            }
          />
        ) : (
          <InfoAlert
            title={warningTitle}
            description={warningDescription}
            extraContent={[
              <Section
                key="info-alert-assets-supported-for-purchase-section"
                headerTitle={replaceTokensInString(
                  YIELD_BEARING_ASSET_ACTIONS_CARD.SUPPORTED_TOKENS_INFORMATION_TITLE,
                  replacements,
                )}
                body={replaceTokensInString(
                  YIELD_BEARING_ASSET_ACTIONS_CARD.SUPPORTED_TOKENS_INFORMATION_DESCRIPTION,
                  {
                    ...replacements,
                    supported_networks: generateEthereumNetworkDescription({
                      showEthereumOnly: true,
                    }),
                  },
                )}
              />,
              <DepositToWalletButton key="deposit-button" fullWidth />,
            ]}
          />
        )}
        {yieldBearingAsset?.underlyingYieldBearingAssets?.length > 1 &&
          depositData.underlyingYieldBearingAssetsAllocation &&
          areAllocationsDifferent(
            yieldBearingAsset.underlyingYieldBearingAssets,
            depositData.underlyingYieldBearingAssetsAllocation,
          ) && (
            <YieldBearingAssetCompositionSection
              yieldBearingAsset={yieldBearingAsset}
              underlyingYieldBearingAssets={
                depositData.underlyingYieldBearingAssetsAllocation
              }
              isCompositionDifferentFromOriginal={true}
              showDetails
              showAssetDetailsAsChips={false}
              showSource={false}
              showDescription={true}
              showAssetDescription={false}
              headerTitle={`Personalized Composition`}
              showCompositionDescription={true}
              showCompositionChart={true}
              showDefaultAndPersonalizedReturn
            />
          )}
        {showProjection && (
          <ProjectionChartSection
            projectionData={projectionData}
            title="Projection"
            growthRate={effectiveApy}
            chartId={`${yieldBearingAsset?.ticker}DepositForm`}
            extraContent={
              <InfoAlert
                title="What this means."
                description={generateWhatProjectionMeansMessage(
                  endingAmountUSD,
                  startingAmountUSD,
                  depositData.amount || 0,
                  depositData.token?.symbol || 'USD',
                  projectionData.length - 1,
                  totalReturnPercentage,
                )}
              />
            }
            noDataNode={
              <InfoAlert
                title={replaceTokensInString(
                  YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
                    .PROJECTION_CHART.NO_ASSET_OR_AMOUNT_IS_PRESENT_TITLE,
                  replacements,
                )}
                description={replaceTokensInString(
                  YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
                    .PROJECTION_CHART.NO_ASSET_OR_AMOUNT_IS_PRESENT_DESCRIPTION,
                  replacements,
                )}
              />
            }
          />
        )}
        {historicalPerformanceCarousel}
        <div
          className={`mt-auto flex flex-row 
            ${spacingConfig.gapBetweenButtonsInFooter}
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {yieldBearingAsset?.underlyingYieldBearingAssets?.length > 1 && (
            <CompositionButton
              clickHandler={handleNavigatingToAllocationConfiguration}
            />
          )}
          <Button
            fullWidth
            onClick={handleSubmit(onSubmit)}
            disabled={!hasTokensAvailableForDeposit}
          >
            {
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_FORM
                .EARN_BUTTON_LABEL
            }
          </Button>
        </div>
      </div>
    </ReactHookFormProvider>
  );
};
export default YieldBearingAssetInvestmentDepositForm;
